import React, { Fragment, ReactNode } from 'react';
import {
    Button,
    Grid,
    Tooltip,
    IconButton,
    Link,
    styled,
} from '@mui/material';
import Icon from '@mdi/react';
import { mdiArrowLeft, mdiFilterVariant } from '@mdi/js';

import { useAppDispatch } from '../../../state/hooks';
import { openFilterPanel } from '../../../state/features/filterSlice';
import { getEngageLaneLink } from '../../../utils/navigationHelpers';
import ActiveFilters from '../../Filters/ActiveFilters';
import Hidden from '../../layout/hidden';

import { ReactComponent as EngageLaneBlue } from '../../../assets/images/engage-lane-blue.svg';

const classesPrefix = 'detailHeader';

const classes = {
    header: `${classesPrefix}-header`,
    headerBtn: `${classesPrefix}-headerBtn`,
    engageLaneLogo: `${classesPrefix}-engageLaneLogo`,
    flexEndContainer: `${classesPrefix}-flexEndContainer`,
};

const StyledGrid = styled(Grid)(({ theme }) => {
    return {
        [`&.${classes.header}`]: {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.getContrastText(theme.palette.primary.main),
            minHeight: theme.spacing(6),
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
        },
        [`& .${classes.headerBtn}`]: {
            color: theme.palette.getContrastText(theme.palette.primary.main),
            margin: theme.spacing(1),
            fill: '#fff',
            '&:hover': {
                color: theme.palette.primary.main,
                backgroundColor: theme.palette.common.white,
                fill: '#00619F',
            },
            '&:disabled': {
                cursor: 'not-allowed',
                opacity: 0.75,
                color: theme.palette.secondary.main,
                backgroundColor: theme.palette.common.white,
            },
        },
        [`& .${classes.engageLaneLogo}`]: {
            fill: 'inherit',
        },
        [`& .${classes.flexEndContainer}`]: {
            flexGrow: 1,
            display: 'flex',
            justifyContent: 'flex-end',
        },
        [theme.breakpoints.down('sm')]: {
            [`& .${classes.headerBtn}`]: {
                margin: theme.spacing(0),
            },
        },
    };
});

const DetailHeader = ({
    onBackClicked,
    children,
}: {
    onBackClicked: () => void;
    children?: ReactNode;
}): React.JSX.Element => {
    const dispatch = useAppDispatch();

    return (
        <StyledGrid
            item
            xs='auto'
            className={classes.header}
        >
            <Tooltip title='Go Back' placement='top'>
                <IconButton
                    size='small'
                    onClick={(): void => {
                        onBackClicked();
                    }}
                    className={classes.headerBtn}
                >
                    <Icon path={mdiArrowLeft} size={1} />
                </IconButton>
            </Tooltip>
            <Fragment>
                {children}

                <Hidden direction='down' breakpoint='md'>
                    <ActiveFilters />
                </Hidden>

                <div className={classes.flexEndContainer}>
                    <Tooltip title='Level up with Engage Lane' placement='top'>
                        <span>
                            <Hidden direction='down' breakpoint='md'>
                                <Button
                                    className={classes.headerBtn}
                                    size='small'
                                    component={Link}
                                    underline='none'
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    href={getEngageLaneLink()}
                                    startIcon={
                                        <EngageLaneBlue
                                            className={classes.engageLaneLogo}
                                            width='24px'
                                            height='24px'
                                        />
                                    }
                                >
                                    Engage Lane
                                </Button>
                            </Hidden>

                            <Hidden direction='up' breakpoint='md'>
                                <IconButton
                                    className={classes.headerBtn}
                                    size='small'
                                    component={Link}
                                    underline='none'
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    href={getEngageLaneLink()}
                                >
                                    <EngageLaneBlue
                                        className={classes.engageLaneLogo}
                                        width='24px'
                                        height='24px'
                                    />
                                </IconButton>
                            </Hidden>
                        </span>
                    </Tooltip>
                    <Tooltip title='Filters' placement='top'>
                        <span>
                            <IconButton
                                size='small'
                                onClick={(): void => {
                                    dispatch(openFilterPanel());
                                }}
                                className={classes.headerBtn}
                            >
                                <Icon path={mdiFilterVariant} size={1} />
                            </IconButton>
                        </span>
                    </Tooltip>
                </div>
            </Fragment>
        </StyledGrid>
    );
};

export default DetailHeader;
